import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../../../utils/apiBase.js';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';

function renderBody(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null;
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        elements.push(<ol key={key++}>{listItems}</ol>);
      } else {
        elements.push(<ul key={key++}>{listItems}</ul>);
      }
      listItems = [];
      listType = null;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }

    const olMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
    const ulMatch = trimmed.match(/^[-・]\s+(.*)/);

    if (olMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={key++}>{olMatch[2]}</li>);
    } else if (ulMatch) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={key++}>{ulMatch[1]}</li>);
    } else {
      flushList();
      elements.push(<p key={key++}>{trimmed}</p>);
    }
  }
  flushList();

  return elements;
}

const LegalPageDynamic = ({
  docType,
  fallbackTitle,
  fallbackContent,
  backTo = '/',
  backLabel = 'トップに戻る',
  embedded = false,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const currentYear = new Date().getFullYear();
  const pageClassName = embedded ? 'hub-legal-page hub-legal-page--embedded' : 'hub-legal-page';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(buildApiUrl(`/api/legal/${docType}`));
        if (!res.ok) throw new Error('fetch failed');
        const json = await res.json();
        if (!cancelled) {
          if (json.articles && json.articles.length > 0) {
            setData(json);
          } else {
            setError(true);
          }
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [docType]);

  const formatDate = (d) => {
    if (!d) return null;
    try {
      const date = new Date(d);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    } catch {
      return String(d);
    }
  };

  if (loading) {
    return (
      <div className={pageClassName}>
        <div className="hub-legal-container" style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return fallbackContent || null;
  }

  return (
    <div className={pageClassName}>
      <div className="hub-legal-container">
        <div className="hub-legal-header">
          <Link to={backTo} className="hub-legal-back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {backLabel}
          </Link>
          <h1>{data.title || fallbackTitle}</h1>
          {data.lastPublishedAt && (
            <p className="hub-legal-updated">最終更新日: {formatDate(data.lastPublishedAt)}</p>
          )}
        </div>

        <div className="hub-legal-content">
          {data.articles.map((article, index) => (
            <section key={`${article.order}-${index}`} className="hub-legal-section">
              {article.heading && <h2>{article.heading}</h2>}
              {renderBody(article.body)}
              {(article.subArticles || []).map((sub, si) => (
                <React.Fragment key={si}>
                  {sub.heading && <h3>{sub.heading}</h3>}
                  {renderBody(sub.body)}
                </React.Fragment>
              ))}
            </section>
          ))}
        </div>

        <div className="hub-legal-footer">
          <p>&copy; {currentYear} Synapse. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default LegalPageDynamic;
